import { useState, useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { galleryImages } from "@/lib/data/gallery";
import { GALLERY_CATEGORIES } from "@/lib/constants";
import Lightbox from "./Lightbox";
import type { GalleryCategory } from "@/types";

export default function Gallery() {
  const [selectedCategory, setSelectedCategory] =
    useState<GalleryCategory>("All");
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredImages =
    selectedCategory === "All"
      ? galleryImages
      : galleryImages.filter((image) => image.category === selectedCategory);

  // On mobile (showAll state matters), on desktop always show all
  const displayedImages = filteredImages.slice(0, showAll ? filteredImages.length : 6);
  const shouldShowToggle = filteredImages.length > 6;

  const openLightbox = (imageId: number) => {
    setSelectedImage(imageId);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction: "prev" | "next") => {
    if (selectedImage === null) return;

    const currentIndex = filteredImages.findIndex(
      (img) => img.id === selectedImage
    );
    let newIndex;

    if (direction === "prev") {
      newIndex = currentIndex > 0 ? currentIndex - 1 : filteredImages.length - 1;
    } else {
      newIndex = currentIndex < filteredImages.length - 1 ? currentIndex + 1 : 0;
    }

    setSelectedImage(filteredImages[newIndex]?.id ?? null);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-gray-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
            Gallery
          </h1>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            A comprehensive collection of my photographic work spanning various
            genres and subjects, each image telling its own unique story.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {GALLERY_CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  selectedCategory === category
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Mobile: Limited view, Desktop: All images */}
          <div className="grid grid-cols-1 gap-6 sm:hidden">
            {displayedImages.map((image) => (
              <div
                key={image.id}
                className="group cursor-pointer"
                onClick={() => openLightbox(image.id)}
              >
                <div className="relative overflow-hidden bg-gray-100 aspect-[4/3] mb-4">
                  <img
                    src={image.image}
                    alt={image.alt}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <i className="ri-eye-line text-2xl"></i>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 font-inter leading-tight">{image.title}</h3>
                  <p className="text-sm text-gray-500 leading-tight">{image.category} | {image.year} | {image.location}</p>
                  <p className="text-sm text-gray-700 leading-tight mt-2">{image.caption}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Always show all images */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filteredImages.map((image) => (
              <div
                key={image.id}
                className="group cursor-pointer"
                onClick={() => openLightbox(image.id)}
              >
                <div className="relative overflow-hidden bg-gray-100 aspect-[4/3] mb-4">
                  <img
                    src={image.image}
                    alt={image.alt}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <i className="ri-eye-line text-2xl"></i>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 font-inter leading-tight">{image.title}</h3>
                  <p className="text-sm text-gray-500 leading-tight">{image.category} | {image.year} | {image.location}</p>
                  <p className="text-sm text-gray-700 leading-tight mt-2">{image.caption}</p>
                </div>
              </div>
            ))}
          </div>

          {filteredImages.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No images found in this category.</p>
            </div>
          )}

          {/* Show More/Less Button - Mobile Only */}
          {shouldShowToggle && (
            <div className="text-center mt-8 sm:hidden">
              <button
                onClick={() => setShowAll(!showAll)}
                className="inline-flex items-center text-gray-900 font-medium hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {showAll ? 'Show Less' : 'See All Work'}
                <div className="w-4 h-4 ml-2 flex items-center justify-center">
                  <i className={`ri-arrow-${showAll ? 'up' : 'down'}-line`}></i>
                </div>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <Lightbox
          image={
            filteredImages.find((img) => img.id === selectedImage) ||
            filteredImages[0]!
          }
          onClose={closeLightbox}
          onPrev={() => navigateImage("prev")}
          onNext={() => navigateImage("next")}
          showNavigation={filteredImages.length > 1}
        />
      )}

      <Footer />
    </div>
  );
}
